import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import Layout2 from "@/components/Layout/Layout2";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "react-hot-toast";
import { getEndOfDayReportServiceType } from "@/api/reporting";
import ReportingSummaryTable from "./components/reporting-summary-table";
import { useReportTills } from "./use-report-tills";
import {
  buildReportCsvName,
  buildSummaryRows,
  downloadCsv,
  getTodayInputDate,
  parseTillId,
} from "./reporting-utils";
import hasPermission from "@/hooks/hasPermission";

function EndOfDayReportZServiceType() {
  const [tillFilter, setTillFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(getTodayInputDate());
  const [summaryRows, setSummaryRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { tills } = useReportTills();

  useEffect(() => {
    let isActive = true;

    const fetchReport = async () => {
      setIsLoading(true);
      const tillId = parseTillId(tillFilter);
      const response = await getEndOfDayReportServiceType({
        date: selectedDate || undefined,
        till_id: tillId,
      });

      if (!isActive) return;

      if (response?.status === "success") {
        const data = response?.data || {};
        setSummaryRows(
          buildSummaryRows({
            items: data.service_types,
            netTotal: data.net_total,
          })
        );
      } else {
        toast.error(response?.message || "Failed to load service type report.");
        setSummaryRows([]);
      }

      setIsLoading(false);
    };

    fetchReport();

    return () => {
      isActive = false;
    };
  }, [selectedDate, tillFilter]);

  const handleExportCsv = () => {
    if (!summaryRows.length) {
      toast.error("No data available to export.");
      return;
    }

    const tillId = parseTillId(tillFilter);
    const fileName = buildReportCsvName(
      "end-of-day-z-service-type",
      selectedDate,
      tillId
    );
    const rows = summaryRows.map((row) => [row.label, row.count, row.value]);
    downloadCsv(fileName, [
      "Service Type",
      "Number of Order/ Transactions",
      "Total Value",
    ], rows);
  };

  return (
    <Layout2
      pageName={{ icon: true, title: "End of Day Report/Z (Service Type)" }}
      paths={[
        { name: "Home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Reporting", path: "/reporting" },
        {
          name: "End of Day Report/Z (Service Type)",
          path: "/reporting/end-of-day-z-service-type",
        },
      ]}
    >
      <div className="px-4 pb-12">
        <div className="text-sm font-semibold text-cwhite">Search By Date</div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <div className="w-max-content">
            <Input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="bg-cblack3 text-sm"
            />
          </div>
          <Select value={tillFilter} onValueChange={setTillFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Filter by Till" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Till</SelectItem>
              {tills.length === 0 ? (
                <SelectItem value="no-tills" disabled>
                  No tills available
                </SelectItem>
              ) : (
                tills.map((till) => (
                  <SelectItem key={till.id} value={String(till.id)}>
                    {till.device_name || `Till-${till.id}`}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {hasPermission("reporting.reporting.end_of_day_report_z_print") && (
            <button
              type="button"
              onClick={handleExportCsv}
              className="flex h-9 items-center gap-2 rounded-md bg-cblack6 px-4 text-sm text-cwhite"
            >
              Export as CSV
              <Download size={16} />
            </button>
          )}
        </div>

        <div className="mt-4">
          {isLoading ? (
            <div className="rounded-md border border-cblack6 bg-cblack4 px-4 py-3 text-sm text-cwhite3">
              Loading report data...
            </div>
          ) : summaryRows.length > 0 ? (
            <ReportingSummaryTable
              rows={summaryRows}
              headers={[
                "Service Type",
                "Number of Order/ Transactions",
                "Total Value",
              ]}
            />
          ) : (
            <div className="rounded-md border border-cblack6 bg-cblack4 px-4 py-3 text-sm text-cwhite3">
              No data found for the selected filters.
            </div>
          )}
        </div>
      </div>
    </Layout2>
  );
}

export default EndOfDayReportZServiceType;
